import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {v4 as uuidv4} from 'uuid';

export interface FocusSession {
  id: string;
  entityId: string;
  entityType: 'node' | 'link';
  field: string;
  startValue: string;
  startedAt: number;
}

export interface FocusSessionEndEvent extends FocusSession {
  endValue: string;
}

/**
 * FocusSessionService tracks text field focus sessions for coalescing text changes
 * into single undo actions. All changes made while a text field is focused will be
 * combined into one undoable action.
 */
@Injectable({
  providedIn: 'root'
})
export class FocusSessionService {
  private currentSession: FocusSession | null = null;
  private sessionEnded$ = new Subject<FocusSessionEndEvent>();

  /**
   * Start a new focus session when a text field receives focus.
   * If there's an existing session for a different field, it will be ended first.
   *
   * @param entityId The ID of the node or link being edited
   * @param entityType Whether it's a node or link
   * @param field The field name being edited (e.g., 'name', 'description')
   * @param currentValue The current value of the field when focus started
   * @returns The session ID for this focus session
   */
  startSession(
    entityId: string,
    entityType: 'node' | 'link',
    field: string,
    currentValue: string
  ): string {
    // If there's an existing session for a different entity/field, end it with no change
    if (
      this.currentSession &&
      (this.currentSession.entityId !== entityId ||
        this.currentSession.entityType !== entityType ||
        this.currentSession.field !== field)
    ) {
      // End the previous session without emitting (no value change known)
      this.currentSession = null;
    }

    // If no current session or different context, create new one
    if (
      !this.currentSession ||
      this.currentSession.entityId !== entityId ||
      this.currentSession.entityType !== entityType ||
      this.currentSession.field !== field
    ) {
      this.currentSession = {
        id: uuidv4(),
        entityId,
        entityType,
        field,
        startValue: currentValue,
        startedAt: Date.now()
      };
    }

    return this.currentSession.id;
  }

  /**
   * End the current focus session and emit a session end event if the value changed.
   *
   * @param finalValue The final value of the field when focus was lost
   * @returns The ended session, or null if no session was active
   */
  endSession(finalValue: string): FocusSession | null {
    if (!this.currentSession) {
      return null;
    }

    const session = {...this.currentSession};
    this.currentSession = null;

    // Only emit if value actually changed
    if (session.startValue !== finalValue) {
      this.sessionEnded$.next({
        ...session,
        endValue: finalValue
      });
    }

    return session;
  }

  /**
   * Cancel the current session without emitting an event.
   * Use this when you want to discard changes (e.g., ESC key pressed).
   */
  cancelSession(): void {
    this.currentSession = null;
  }

  /**
   * Get observable for session end events.
   * Only emits when the value actually changed during the session.
   */
  onSessionEnded() {
    return this.sessionEnded$.asObservable();
  }

  /**
   * Get current session ID for command merging.
   */
  getCurrentSessionId(): string | null {
    return this.currentSession?.id ?? null;
  }

  /**
   * Get the current session if one is active.
   */
  getCurrentSession(): FocusSession | null {
    return this.currentSession ? {...this.currentSession} : null;
  }

  /**
   * Check if there's an active focus session.
   */
  hasActiveSession(): boolean {
    return this.currentSession !== null;
  }

  /**
   * Check if the current session matches the given parameters.
   */
  isSessionFor(entityId: string, entityType: 'node' | 'link', field: string): boolean {
    if (!this.currentSession) return false;
    return (
      this.currentSession.entityId === entityId &&
      this.currentSession.entityType === entityType &&
      this.currentSession.field === field
    );
  }
}
